import Phaser from 'phaser';
import type { IsoProjection } from '../core/iso';
import type { Footprint } from '../sim/buildings/definitions';
import { footprintOutline } from '../sim/buildings/footprint';
import type { GridPoint } from '../core/types';
import { passabilityOf, terrainInfo } from '../world/terrain';
import { DecorationType, type WorldMap } from '../world/WorldMap';
import { ChunkGrid, type WorldRect } from './culling';
import { LAYER, objectDepth } from './depth';
import { ART_SCALE, DECORATION_FRAMES, OBJECT_SPECS, OVERLAY_DIAMOND_FRAME, TEX } from './proceduralArt';
import { PASSABILITY_COLORS, UI_COLORS } from './palette';

const SKIRT_DEPTH = 18; // world px of earth visible under the map's front edges

/**
 * Turns WorldMap data into Phaser display objects. Holds no gameplay state;
 * it can be destroyed and rebuilt from the WorldMap at any time (save/load in
 * Phase 8 relies on this).
 */
/** A multi-tile area to outline (a building's footprint). */
export interface MarkArea {
  readonly origin: GridPoint;
  readonly footprint: Footprint;
}

export class MapRenderer {
  /** One image per tile, indexed like WorldMap arrays (y * width + x). */
  private readonly groundTiles: Phaser.GameObjects.Image[] = [];
  private readonly skirt: Phaser.GameObjects.Graphics;
  private readonly grid: Phaser.GameObjects.Graphics;
  /**
   * Passability tint: one tinted sprite per tile in a single Layer, so it is
   * batched like the ground and toggled with one flag. (A Graphics object
   * with 4,096 polygons is re-tessellated every frame and halved the frame
   * rate — see DECISIONS D15.)
   */
  private readonly passability: Phaser.GameObjects.Layer;
  /** Ground-level fills (hidden behind tall objects, like real ground). */
  private readonly hoverGfx: Phaser.GameObjects.Graphics;
  private readonly selectGfx: Phaser.GameObjects.Graphics;
  /** Outlines on the top overlay layer so the marked tile is never lost behind trees. */
  private readonly hoverTop: Phaser.GameObjects.Graphics;
  private readonly selectTop: Phaser.GameObjects.Graphics;
  private readonly pulse: Phaser.Tweens.Tween;
  private readonly objects: Phaser.GameObjects.Image[] = [];
  private readonly extras: Phaser.GameObjects.GameObject[] = [];
  /** Culling: every per-tile sprite (ground, overlay, objects) grouped by chunk. */
  private readonly chunks: ChunkGrid;
  private readonly chunkMembers: Phaser.GameObjects.Image[][];
  private visibleChunks = new Set<number>();
  /** Decoration sprite per tile index, and those hidden under buildings. */
  private readonly decorationByTile = new Map<number, Phaser.GameObjects.Image>();
  private readonly covered = new Set<Phaser.GameObjects.Image>();
  /** How far objects may rise above their tile (world px), so tall sprites never pop. */
  private readonly maxObjectRise: number;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly map: WorldMap,
    private readonly iso: IsoProjection,
    chunkSize: number,
  ) {
    this.chunks = new ChunkGrid(iso, map.width, map.height, chunkSize);
    this.chunkMembers = Array.from({ length: this.chunks.count }, () => []);
    this.maxObjectRise = Math.max(...Object.values(OBJECT_SPECS).map((o) => o.h * o.originY)) + 8;
    this.skirt = scene.add.graphics().setDepth(LAYER.skirt);
    this.passability = scene.add.layer().setDepth(LAYER.groundOverlay).setVisible(false);
    this.grid = scene.add.graphics().setDepth(LAYER.grid).setVisible(false);
    this.hoverGfx = scene.add.graphics().setDepth(LAYER.markers);
    this.selectGfx = scene.add.graphics().setDepth(LAYER.markers + 1);
    this.hoverTop = scene.add.graphics().setDepth(LAYER.overlay);
    this.selectTop = scene.add.graphics().setDepth(LAYER.overlay + 1);

    this.drawSkirt();
    this.drawGround();
    this.drawPassability();
    this.drawGrid();
    this.placeObjects();

    // Gentle pulse on the selection outline. Runs on Phaser's own tween manager
    // (part of the scene's single update loop), paused while nothing is selected.
    this.pulse = scene.tweens.add({
      targets: this.selectTop,
      alpha: { from: 1, to: 0.45 },
      duration: 650,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      paused: true,
    });
  }

  /** World-space bounds of the rendered ground sprite for a tile (validation / tests). */
  groundTileBounds(x: number, y: number): { x: number; y: number; width: number; height: number } | null {
    if (!this.map.inBounds(x, y)) return null;
    const r = this.groundTiles[this.map.index(x, y)]?.getBounds();
    return r ? { x: r.x, y: r.y, width: r.width, height: r.height } : null;
  }

  /** Chunks currently drawn / total (diagnostics). */
  get cullingStats(): { visible: number; total: number } {
    return { visible: this.visibleChunks.size, total: this.chunks.count };
  }

  /**
   * Show only chunks near the camera's world view. Cheap to call on every
   * camera change: it only touches sprites whose chunk changed state.
   */
  updateCulling(view: WorldRect): void {
    const next = this.chunks.visible(view, this.iso.tileWidth, this.maxObjectRise);
    for (const i of this.visibleChunks) if (!next.has(i)) for (const o of this.chunkMembers[i]!) o.setVisible(false);
    for (const i of next) if (!this.visibleChunks.has(i)) for (const o of this.chunkMembers[i]!) o.setVisible(!this.covered.has(o));
    this.visibleChunks = next;
  }

  get objectCount(): number {
    return this.objects.length;
  }

  get gridVisible(): boolean {
    return this.grid.visible;
  }

  setGridVisible(v: boolean): void {
    this.grid.setVisible(v);
  }

  get passabilityVisible(): boolean {
    return this.passability.visible;
  }

  setPassabilityVisible(v: boolean): void {
    this.passability.setVisible(v);
  }

  /**
   * Hovered tile (or null). When `area` is given (a building under the
   * cursor), the whole footprint is outlined instead of the single tile.
   */
  setHover(tile: GridPoint | null, area: MarkArea | null = null): void {
    this.hoverGfx.clear();
    this.hoverTop.clear();
    if (!tile) return;
    const pts = area ? this.areaPoints(area) : this.tilePoints(tile);
    this.hoverGfx.fillStyle(UI_COLORS.hover, area ? 0.1 : 0.18);
    this.hoverGfx.fillPoints(pts, true);
    this.hoverTop.lineStyle(1.5, UI_COLORS.hover, 0.85);
    this.hoverTop.strokePoints(pts, true, true);
  }

  /**
   * Selected tile (or null): ground fill + always-visible pulsing outline with
   * corner pins. With `area` (a selected building) the outline wraps the
   * whole footprint.
   */
  setSelected(tile: GridPoint | null, area: MarkArea | null = null): void {
    this.selectGfx.clear();
    this.selectTop.clear();
    if (!tile) {
      this.pulse.pause();
      this.selectTop.setAlpha(1);
      return;
    }
    const pts = area ? this.areaPoints(area) : this.tilePoints(tile);
    this.selectGfx.fillStyle(UI_COLORS.select, area ? 0.16 : 0.28);
    this.selectGfx.fillPoints(pts, true);
    this.selectTop.lineStyle(2.5, UI_COLORS.select, 1);
    this.selectTop.strokePoints(pts, true, true);
    this.selectTop.fillStyle(UI_COLORS.select, 1);
    for (const p of pts) this.selectTop.fillCircle(p.x, p.y, 3);
    if (this.pulse.isPaused()) this.pulse.resume();
    else this.pulse.restart();
  }

  /**
   * Hide (covered = true) or restore the decorations on tiles a building now
   * occupies, so trees and bushes never poke through it. Works with culling:
   * covered decorations stay hidden even when their chunk becomes visible.
   */
  setTilesCovered(tiles: readonly GridPoint[], covered: boolean): void {
    for (const t of tiles) {
      if (!this.map.inBounds(t.x, t.y)) continue;
      const img = this.decorationByTile.get(this.map.index(t.x, t.y));
      if (!img) continue;
      if (covered) {
        this.covered.add(img);
        img.setVisible(false);
      } else {
        this.covered.delete(img);
        img.setVisible(this.visibleChunks.has(this.chunks.chunkOf(t.x, t.y)));
      }
    }
  }

  private tilePoints(tile: GridPoint): Phaser.Math.Vector2[] {
    return this.iso.tileCorners(tile.x, tile.y) as unknown as Phaser.Math.Vector2[];
  }

  private areaPoints(area: MarkArea): Phaser.Math.Vector2[] {
    return footprintOutline(area.origin, area.footprint).map((g) => this.iso.gridToWorld(g.x, g.y)) as unknown as Phaser.Math.Vector2[];
  }

  destroy(): void {
    for (const t of this.groundTiles) t.destroy();
    this.groundTiles.length = 0;
    this.skirt.destroy();
    this.grid.destroy();
    this.pulse.remove();
    this.passability.destroy(true);
    this.hoverGfx.destroy();
    this.selectGfx.destroy();
    this.hoverTop.destroy();
    this.selectTop.destroy();
    for (const o of this.objects) o.destroy();
    for (const o of this.extras) o.destroy();
    this.objects.length = 0;
    this.extras.length = 0;
    for (const m of this.chunkMembers) m.length = 0;
    this.decorationByTile.clear();
    this.covered.clear();
    this.visibleChunks.clear();
  }

  // ------------------------------------------------------------------

  /**
   * Ground tiles are individual images (not a Blitter: Phaser Blitters ignore
   * scale, and the art is authored at ART_SCALE). All share one texture and
   * one depth, so WebGL batches them; Phaser's stable depth sort keeps the
   * painter's order (back rows first) we add them in.
   */
  private drawGround(): void {
    const { width, height } = this.map;
    this.groundTiles.length = width * height;
    for (let sum = 0; sum <= width + height - 2; sum++) {
      for (let x = Math.max(0, sum - height + 1); x <= Math.min(width - 1, sum); x++) {
        const y = sum - x;
        const c = this.iso.gridToWorld(x, y);
        const img = this.scene.add
          .image(c.x, c.y, TEX.ground, this.groundFrame(x, y))
          .setScale(1 / ART_SCALE)
          .setDepth(LAYER.ground);
        this.groundTiles[this.map.index(x, y)] = img;
        this.track(img, x, y);
      }
    }
  }

  /** Re-skin one tile after its terrain changes (used by later phases). */
  refreshTile(x: number, y: number): void {
    this.groundTiles[this.map.index(x, y)]?.setFrame(this.groundFrame(x, y));
  }

  /** Tint every tile by passability class (toggled with P / the HUD button). */
  private drawPassability(): void {
    const { width, height } = this.map;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const { color, alpha } = PASSABILITY_COLORS[passabilityOf(this.map.info(x, y))];
        const c = this.iso.gridToWorld(x, y);
        const img = this.scene.make
          .image({ x: c.x, y: c.y, key: TEX.ground, frame: OVERLAY_DIAMOND_FRAME }, false)
          .setScale(1 / ART_SCALE)
          .setTint(color)
          .setAlpha(alpha);
        this.passability.add(img);
        this.track(img, x, y);
      }
    }
  }

  private groundFrame(x: number, y: number): string {
    return `${terrainInfo(this.map.getTerrain(x, y)).key}_${this.map.variant[this.map.index(x, y)]}`;
  }

  /** Earth "cut" along the two front edges so the map reads as a raised board. */
  private drawSkirt(): void {
    const g = this.skirt;
    const { width, height } = this.map;
    const top = Phaser.Display.Color.HexStringToColor(UI_COLORS.skirtTop).color;
    const bottom = Phaser.Display.Color.HexStringToColor(UI_COLORS.skirtBottom).color;
    const face = (ax: number, ay: number, bx: number, by: number, shade: number): void => {
      g.fillGradientStyle(top, top, bottom, bottom, 1);
      g.fillPoints([
        { x: ax, y: ay }, { x: bx, y: by }, { x: bx, y: by + SKIRT_DEPTH }, { x: ax, y: ay + SKIRT_DEPTH },
      ] as Phaser.Math.Vector2[], true);
      g.fillStyle(0x000000, shade);
      g.fillPoints([
        { x: ax, y: ay }, { x: bx, y: by }, { x: bx, y: by + SKIRT_DEPTH }, { x: ax, y: ay + SKIRT_DEPTH },
      ] as Phaser.Math.Vector2[], true);
    };
    // South-west face: along y = height-1 (left corner → bottom corner).
    const left = this.iso.tileCorners(0, height - 1)[3];
    const bottomC = this.iso.tileCorners(width - 1, height - 1)[2];
    const right = this.iso.tileCorners(width - 1, 0)[1];
    face(left.x, left.y, bottomC.x, bottomC.y, 0);
    face(bottomC.x, bottomC.y, right.x, right.y, 0.22);
    // Strata lines.
    g.lineStyle(1, 0x2a1d12, 0.45);
    for (const f of [0.45, 0.75]) {
      g.lineBetween(left.x, left.y + SKIRT_DEPTH * f, bottomC.x, bottomC.y + SKIRT_DEPTH * f);
      g.lineBetween(bottomC.x, bottomC.y + SKIRT_DEPTH * f, right.x, right.y + SKIRT_DEPTH * f);
    }
    // Always-visible map boundary outline, drawn just above the ground.
    const outline = this.scene.add.graphics().setDepth(LAYER.boundary);
    const topC = this.iso.tileCorners(0, 0)[0];
    outline.lineStyle(2, 0x1d140c, 0.55);
    outline.strokePoints([topC, right, bottomC, left] as Phaser.Math.Vector2[], true, true);
    this.extras.push(outline);
  }

  private drawGrid(): void {
    const g = this.grid;
    const { width, height } = this.map;
    g.lineStyle(1, UI_COLORS.grid, 0.22);
    for (let x = 0; x <= width; x++) {
      const a = this.iso.gridToWorld(x - 0.5, -0.5);
      const b = this.iso.gridToWorld(x - 0.5, height - 0.5);
      g.lineBetween(a.x, a.y, b.x, b.y);
    }
    for (let y = 0; y <= height; y++) {
      const a = this.iso.gridToWorld(-0.5, y - 0.5);
      const b = this.iso.gridToWorld(width - 0.5, y - 0.5);
      g.lineBetween(a.x, a.y, b.x, b.y);
    }
  }

  private placeObjects(): void {
    const { width, height } = this.map;
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const deco = this.map.getDecoration(x, y);
        if (deco === DecorationType.None) continue;
        const frame = DECORATION_FRAMES[deco];
        if (!frame) continue;
        this.addObject(frame, x, y, deco === DecorationType.Flowers || deco === DecorationType.Infestation);
      }
    }
  }

  private addObject(frame: string, x: number, y: number, flat: boolean): void {
    const spec = OBJECT_SPECS[frame];
    if (!spec) throw new Error(`Unknown object frame ${frame}`);
    const c = this.iso.gridToWorld(x, y);
    const img = this.scene.add
      .image(c.x, c.y, TEX.objects, frame)
      .setOrigin(spec.originX, spec.originY)
      .setScale(1 / ART_SCALE)
      // Flat decals lie on the ground; upright objects sort by their base (see depth.ts).
      .setDepth(flat ? LAYER.groundDecal : objectDepth(this.iso, x, y));
    this.objects.push(img);
    this.decorationByTile.set(this.map.index(x, y), img);
    this.track(img, x, y);
  }

  /** Register a per-tile sprite for culling; it starts hidden until its chunk is in view. */
  private track(img: Phaser.GameObjects.Image, x: number, y: number): void {
    img.setVisible(false);
    this.chunkMembers[this.chunks.chunkOf(x, y)]!.push(img);
  }
}
